import React, { useState, useEffect } from 'react';
import {
  Users,
  Heart,
  MessageSquare,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Activity,
  UserCheck,
  UserX
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const StatCard = ({ title, value, icon: Icon, change, color = 'purple' }) => {
  const colorClasses = {
    purple: 'bg-purple-600',
    green: 'bg-green-600',
    blue: 'bg-blue-600',
    yellow: 'bg-yellow-600',
    red: 'bg-red-600',
    pink: 'bg-pink-600'
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {change >= 0 ? '+' : ''}{change}% from last week
            </p>
          )}
        </div>
        <div className={`w-12 h-12 ${colorClasses[color]} rounded-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, activityRes, auditRes] = await Promise.all([
        api.get('/admin/dashboard/stats', config),
        api.get('/admin/dashboard/recent-activity?limit=10', config),
        api.get('/admin/dashboard/audit-logs?limit=10', config)
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Dashboard</h1>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users?.total?.toLocaleString() || 0}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="New Users (Today)"
          value={stats?.users?.new_today || 0}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Active Users (24h)"
          value={stats?.users?.active_24h || 0}
          icon={Activity}
          color="blue"
        />
        <StatCard
          title="Verified Users"
          value={stats?.users?.verified || 0}
          icon={UserCheck}
          color="pink"
        />
      </div>

      {/* Engagement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Matches"
          value={stats?.engagement?.total_matches?.toLocaleString() || 0}
          icon={Heart}
          color="pink"
        />
        <StatCard
          title="New Matches (Today)"
          value={stats?.engagement?.new_matches_today || 0}
          icon={Heart}
          color="red"
        />
        <StatCard
          title="Total Messages"
          value={stats?.engagement?.total_messages?.toLocaleString() || 0}
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="Pending Reports"
          value={stats?.moderation?.pending_reports || 0}
          icon={AlertTriangle}
          color="yellow"
        />
      </div>

      {/* Revenue & Subscription Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Subscriptions"
          value={stats?.subscriptions?.active_total || 0}
          icon={CreditCard}
          color="purple"
        />
        <StatCard
          title="New Subs (Today)"
          value={stats?.subscriptions?.new_today || 0}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Revenue (Today)"
          value={`${stats?.revenue?.today?.toLocaleString() || 0} ETB`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Revenue (Month)"
          value={`${stats?.revenue?.month?.toLocaleString() || 0} ETB`}
          icon={DollarSign}
          color="blue"
        />
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Distribution */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Gender Distribution</h2>
          <div className="space-y-3">
            {stats?.users?.gender_distribution && Object.entries(stats.users.gender_distribution).map(([gender, count]) => (
              <div key={gender} className="flex items-center justify-between">
                <span className="text-gray-400 capitalize">{gender || 'Unknown'}</span>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${(count / stats.users.total) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium w-16 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subscriptions by Plan */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Subscriptions by Plan</h2>
          <div className="space-y-3">
            {stats?.subscriptions?.by_plan && Object.entries(stats.subscriptions.by_plan).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <span className="text-gray-400 capitalize">{plan}</span>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${(count / (stats.subscriptions.active_total || 1)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium w-16 text-right">{count}</span>
                </div>
              </div>
            ))}
            {(!stats?.subscriptions?.by_plan || Object.keys(stats.subscriptions.by_plan).length === 0) && (
              <p className="text-gray-500 text-center py-4">No active subscriptions</p>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Conversion Rate</span>
              <span className="text-white font-medium">{stats?.subscriptions?.conversion_rate || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 flex items-center space-x-4">
          <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Active Users</p>
            <p className="text-xl font-bold text-white">
              {(stats?.users?.total || 0) - (stats?.users?.suspended || 0) - (stats?.users?.banned || 0)}
            </p>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 flex items-center space-x-4">
          <div className="w-12 h-12 bg-yellow-600/20 rounded-lg flex items-center justify-center">
            <UserX className="w-6 h-6 text-yellow-500" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Suspended Users</p>
            <p className="text-xl font-bold text-white">{stats?.users?.suspended || 0}</p>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 flex items-center space-x-4">
          <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center">
            <UserX className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Banned Users</p>
            <p className="text-xl font-bold text-white">{stats?.users?.banned || 0}</p>
          </div>
        </div>
      </div>

      {/* Admin Audit Log */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Admin Actions</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm">
                <th className="pb-3">Admin</th>
                <th className="pb-3">Action</th>
                <th className="pb-3">Target</th>
                <th className="pb-3">Time</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {auditLogs.map((log) => (
                <tr key={log.id} className="border-t border-gray-700">
                  <td className="py-3">{log.admin_username}</td>
                  <td className="py-3">
                    <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                      {log.action.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="py-3 text-sm">{log.target_type}</td>
                  <td className="py-3 text-sm text-gray-500">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-gray-500">
                    No recent admin actions
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
